import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Percent, Receipt, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', bg: '#F4F6F9' };
const money = (v) => `$${Math.round(v || 0).toLocaleString()}`;
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

function Chip({ ok, children, testid }) {
  return (
    <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid={testid}
      style={{ background: ok ? '#E7F6EF' : '#FDF7EC', color: ok ? C.green : C.amber }}>{children}</span>
  );
}

const btn = 'px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white disabled:opacity-50 flex items-center gap-1.5';

export const CommissionScheduleCard = ({ api, canEdit }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ broker_pct: '', mga_pct: '', basis: '' });
  const [bk, setBk] = useState({ name: '', broker_pct: '', mga_pct: '', basis: '' });
  const [fam, setFam] = useState({});
  const [famBasis, setFamBasis] = useState('');
  const [busy, setBusy] = useState('');

  const take = useCallback((d) => {
    setData(d);
    const by = d?.default?.by_family || {};
    setFam(Object.fromEntries((d?.families || []).map(f => [f.id, { broker_pct: by[f.id]?.broker_pct ?? d?.default?.broker_pct ?? '', mga_pct: by[f.id]?.mga_pct ?? d?.default?.mga_pct ?? '' }])));
  }, []);
  const load = useCallback(() => api.get('/commission-schedule').then(r => take(r.data)).catch(() => {}), [api, take]);
  useEffect(() => { load(); }, [load]);

  const saveDefault = async () => {
    setBusy('default');
    try {
      const { data: d } = await api.put('/commission-schedule/default', { broker_pct: Number(form.broker_pct), mga_pct: Number(form.mga_pct), basis: form.basis });
      take(d); setForm({ broker_pct: '', mga_pct: '', basis: '' }); toast.success('Default commission schedule recorded');
    } catch (e) { err(e, 'The schedule was not recorded'); } finally { setBusy(''); }
  };
  const saveFamilies = async () => {
    setBusy('families');
    try {
      const by_family = Object.fromEntries(Object.entries(fam).filter(([, v]) => v.broker_pct !== '').map(([k, v]) => [k, { broker_pct: Number(v.broker_pct), mga_pct: Number(v.mga_pct || 0) }]));
      const { data: d } = await api.put('/commission-schedule/default/families', { by_family, basis: famBasis });
      take(d); setFamBasis(''); toast.success('Per-family brokerage recorded — new quotes price on it');
    } catch (e) { err(e, 'The family rates were not recorded'); } finally { setBusy(''); }
  };
  const clearDefault = async () => {
    setBusy('clear');
    try {
      const { data: d } = await api.post('/commission-schedule/default/clear', { reason: 'Cleared from Admin → Config by the administrator' });
      take(d); toast.success('Default cleared — new quotes will say "not recorded"');
    } catch (e) { err(e, 'The default was not cleared'); } finally { setBusy(''); }
  };
  const saveBrokerage = async () => {
    setBusy('brokerage');
    try {
      const { data: d } = await api.put(`/commission-schedule/brokerages/${encodeURIComponent(bk.name)}`, { broker_pct: Number(bk.broker_pct), mga_pct: Number(bk.mga_pct), basis: bk.basis });
      take(d); setBk({ name: '', broker_pct: '', mga_pct: '', basis: '' }); toast.success(`Schedule recorded for ${bk.name}`);
    } catch (e) { err(e, 'The brokerage schedule was not recorded'); } finally { setBusy(''); }
  };
  const removeBrokerage = async (name) => {
    setBusy(name);
    try {
      const { data: d } = await api.post(`/commission-schedule/brokerages/${encodeURIComponent(name)}/remove`, { reason: 'Removed from Admin → Config by the administrator' });
      take(d); toast.success(`${name} now follows the platform default`);
    } catch (e) { err(e, 'The schedule was not removed'); } finally { setBusy(''); }
  };

  if (!data) return null;
  const d = data.default;
  const famCount = Object.keys(d?.by_family || {}).length;
  const famDirty = d && Object.entries(fam).some(([k, v]) => Number(v.broker_pct) !== Number(d.by_family?.[k]?.broker_pct ?? d.broker_pct) || Number(v.mga_pct) !== Number(d.by_family?.[k]?.mga_pct ?? d.mga_pct));
  return (
    <Card className="p-5 rounded-sm bg-white space-y-3" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="commission-schedule-card">
      <div className="flex flex-wrap items-center gap-2">
        <Percent className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Commission schedule</h3>
        <Chip ok={!!d} testid="commission-default-chip">{d ? `default ${d.broker_pct}% broker · ${d.mga_pct}% MGA${famCount ? ` · ${famCount} family rates` : ''}` : 'no default recorded'}</Chip>
        <Chip ok testid="commission-ceiling-chip">dial nil → {data.ceiling_pct}% ceiling</Chip>
      </div>
      <p className="text-[11px]" style={{ color: C.slate }}>{data.resolution}</p>
      {d ? (
        <div className="flex flex-wrap items-center gap-3 text-[11px] p-2 rounded-sm" style={{ background: C.bg }} data-testid="commission-default-row">
          <span style={{ color: C.navy }}>Recorded by <b>{d.recorded_by}</b> on {String(d.recorded_at).slice(0, 10)} — {d.basis_note}</span>
          {canEdit && <button onClick={clearDefault} disabled={!!busy} className="ml-auto text-[10px] font-semibold flex items-center gap-1" style={{ color: C.red }} data-testid="commission-default-clear"><Trash2 className="w-3 h-3" />Clear</button>}
        </div>
      ) : (
        <p className="text-[11px]" style={{ color: C.amber }} data-testid="commission-not-recorded-note">
          New quotes carry commission <b>0 — not recorded</b> until a schedule is entered here. Invoices already raised keep the rate they were raised with.
          {data.brokerages_unrecorded.length > 0 && ` ${data.brokerages_unrecorded.length} brokerage(s) on file have no schedule.`}
        </p>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2" data-testid="commission-default-form">
          <Input type="number" placeholder="Broker %" value={form.broker_pct} onChange={e => setForm({ ...form, broker_pct: e.target.value })} className="h-8 w-24 text-[11px] rounded-sm" data-testid="commission-default-broker" />
          <Input type="number" placeholder="MGA %" value={form.mga_pct} onChange={e => setForm({ ...form, mga_pct: e.target.value })} className="h-8 w-24 text-[11px] rounded-sm" data-testid="commission-default-mga" />
          <Input placeholder="Basis — which agreement the rate comes from (min 10 chars)" value={form.basis} onChange={e => setForm({ ...form, basis: e.target.value })} className="h-8 flex-1 min-w-[260px] text-[11px] rounded-sm" data-testid="commission-default-basis" />
          <button onClick={saveDefault} disabled={busy === 'default' || form.broker_pct === '' || form.mga_pct === '' || form.basis.trim().length < 10} className={btn} style={{ background: C.blue }} data-testid="commission-default-save">
            {busy === 'default' && <Loader2 className="w-3 h-3 animate-spin" />}{d ? 'Replace default' : 'Record default'}
          </button>
        </div>
      )}
      <div data-testid="commission-families">
        <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: C.slate }}>Brokerage by product family — what every new quote prices on</p>
        <table className="w-full text-[11px]" data-testid="commission-families-table">
          <thead><tr style={{ color: C.slate }}><th className="text-left font-semibold py-1">Family</th><th className="text-right font-semibold py-1 w-28">Broker %</th><th className="text-right font-semibold py-1 w-28">MGA %</th><th className="text-left font-semibold py-1 pl-3">Basis</th></tr></thead>
          <tbody>
            {(data.effective_default || []).map(r => (
              <tr key={r.family} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`commission-family-${r.family}`}>
                <td className="py-1 font-semibold" style={{ color: C.navy }}>{r.name}</td>
                <td className="py-1 text-right">
                  {canEdit && d ? <Input type="number" min={0} max={data.ceiling_pct} step={0.5} value={fam[r.family]?.broker_pct ?? ''} onChange={e => setFam({ ...fam, [r.family]: { ...fam[r.family], broker_pct: e.target.value } })} className="h-7 w-24 ml-auto text-right text-[11px] rounded-sm font-mono" data-testid={`commission-family-broker-${r.family}`} />
                    : <span className="font-mono" style={{ color: r.recorded ? C.blue : C.amber }} data-testid={`commission-family-broker-value-${r.family}`}>{r.recorded ? `${r.broker_pct}%` : 'not recorded'}</span>}
                </td>
                <td className="py-1 text-right">
                  {canEdit && d ? <Input type="number" min={0} max={data.ceiling_pct} step={0.5} value={fam[r.family]?.mga_pct ?? ''} onChange={e => setFam({ ...fam, [r.family]: { ...fam[r.family], mga_pct: e.target.value } })} className="h-7 w-24 ml-auto text-right text-[11px] rounded-sm font-mono" data-testid={`commission-family-mga-${r.family}`} />
                    : <span className="font-mono" style={{ color: C.slate }}>{r.recorded ? `${r.mga_pct}%` : '—'}</span>}
                </td>
                <td className="py-1 pl-3 font-mono text-[10px]" style={{ color: C.slate }}>{r.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {canEdit && d && (
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="commission-families-form">
            <Input placeholder="Basis for the family rates — the ruling or agreement (min 10 chars)" value={famBasis} onChange={e => setFamBasis(e.target.value)} className="h-8 flex-1 min-w-[260px] text-[11px] rounded-sm" data-testid="commission-families-basis" />
            <button onClick={saveFamilies} disabled={busy === 'families' || !famDirty || famBasis.trim().length < 10} className={btn} style={{ background: C.blue }} data-testid="commission-families-save">
              {busy === 'families' && <Loader2 className="w-3 h-3 animate-spin" />}Record family rates
            </button>
          </div>
        )}
        {data.dial && <p className="text-[10px] mt-1" style={{ color: C.slate }} data-testid="commission-dial-rule">Broker's dial on a quote: {data.dial.min_pct}% ("{data.dial.nett_label}") to {data.dial.max_pct}%; a reason is written {data.dial.reason_required}. The client's premium moves by the brokerage dollars; the MGA's net does not.</p>}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: C.slate }}>Per-brokerage schedules ({data.brokerages.length})</p>
        {data.brokerages.length === 0 && <p className="text-[11px]" style={{ color: C.slate }} data-testid="commission-brokerages-empty">None recorded — every brokerage follows the default.</p>}
        {data.brokerages.map(b => (
          <div key={b.brokerage_name} className="flex flex-wrap items-center gap-3 text-[11px] py-1 border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`commission-brokerage-${b.brokerage_name}`}>
            <span className="font-semibold" style={{ color: C.navy }}>{b.brokerage_name}</span>
            <span className="font-mono" style={{ color: C.blue }}>{b.broker_pct}% / {b.mga_pct}%</span>
            <span style={{ color: C.slate }}>{b.basis_note}</span>
            {canEdit && <button onClick={() => removeBrokerage(b.brokerage_name)} disabled={!!busy} className="ml-auto text-[10px] font-semibold" style={{ color: C.red }} data-testid={`commission-brokerage-remove-${b.brokerage_name}`}>Remove</button>}
          </div>
        ))}
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="commission-brokerage-form">
            <Input list="brokerage-names" placeholder="Brokerage" value={bk.name} onChange={e => setBk({ ...bk, name: e.target.value })} className="h-8 w-56 text-[11px] rounded-sm" data-testid="commission-brokerage-name" />
            <datalist id="brokerage-names">{data.brokerages_known.map(n => <option key={n} value={n} />)}</datalist>
            <Input type="number" placeholder="Broker %" value={bk.broker_pct} onChange={e => setBk({ ...bk, broker_pct: e.target.value })} className="h-8 w-24 text-[11px] rounded-sm" data-testid="commission-brokerage-broker" />
            <Input type="number" placeholder="MGA %" value={bk.mga_pct} onChange={e => setBk({ ...bk, mga_pct: e.target.value })} className="h-8 w-24 text-[11px] rounded-sm" data-testid="commission-brokerage-mga" />
            <Input placeholder="Basis (TOBA / agreement, min 10 chars)" value={bk.basis} onChange={e => setBk({ ...bk, basis: e.target.value })} className="h-8 flex-1 min-w-[200px] text-[11px] rounded-sm" data-testid="commission-brokerage-basis" />
            <button onClick={saveBrokerage} disabled={busy === 'brokerage' || !bk.name.trim() || bk.broker_pct === '' || bk.mga_pct === '' || bk.basis.trim().length < 10} className={btn} style={{ background: C.navy }} data-testid="commission-brokerage-save">Record</button>
          </div>
        )}
      </div>
    </Card>
  );
};

export const OperatingExpensesCard = ({ api, canEdit }) => {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([{ label: '', amount: '' }]);
  const [period, setPeriod] = useState('annual');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(() => api.get('/operating-expenses').then(r => setData(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);

  const total = items.reduce((a, i) => a + (Number(i.amount) || 0), 0);
  const save = async () => {
    setBusy('save');
    try {
      const line_items = items.filter(i => i.label.trim() && i.amount !== '').map(i => ({ label: i.label.trim(), amount: Number(i.amount) }));
      const { data: d } = await api.put('/operating-expenses', { line_items, period_label: period, note });
      setData(d); setItems([{ label: '', amount: '' }]); setNote(''); toast.success(`Operating expenses recorded — ${money(d.annual_total)}`);
    } catch (e) { err(e, 'The figure was not recorded'); } finally { setBusy(''); }
  };
  const clear = async () => {
    setBusy('clear');
    try {
      const { data: d } = await api.post('/operating-expenses/clear', { reason: 'Cleared from Admin → Config by the administrator' });
      setData(d); toast.success('Operating expenses cleared — Finance shows "not recorded" again');
    } catch (e) { err(e, 'Not cleared'); } finally { setBusy(''); }
  };

  if (!data) return null;
  return (
    <Card className="p-5 rounded-sm bg-white space-y-3" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="opex-card">
      <div className="flex flex-wrap items-center gap-2">
        <Receipt className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Operating expenses</h3>
        <Chip ok={data.recorded} testid="opex-chip">{data.recorded ? `${money(data.annual_total)} · ${data.period_label}` : 'not recorded'}</Chip>
      </div>
      <p className="text-[11px]" style={{ color: C.slate }} data-testid="opex-basis">{data.basis}</p>
      {data.recorded && (
        <div className="p-2 rounded-sm text-[11px] space-y-0.5" style={{ background: C.bg }} data-testid="opex-current">
          {data.line_items.map(i => <div key={i.label} className="flex justify-between"><span style={{ color: C.navy }}>{i.label}</span><span className="font-mono">{money(i.amount)}</span></div>)}
          <div className="flex justify-between font-semibold border-t pt-1" style={{ borderColor: 'rgba(136,150,168,0.15)', color: C.navy }}><span>Total</span><span className="font-mono">{money(data.annual_total)}</span></div>
          <p style={{ color: C.slate }}>{data.note}</p>
          {canEdit && <button onClick={clear} disabled={!!busy} className="text-[10px] font-semibold flex items-center gap-1" style={{ color: C.red }} data-testid="opex-clear"><Trash2 className="w-3 h-3" />Clear</button>}
        </div>
      )}
      {canEdit && (
        <div className="space-y-2" data-testid="opex-form">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Line item (e.g. Salaries)" value={it.label} onChange={e => setItems(items.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="h-8 flex-1 text-[11px] rounded-sm" data-testid={`opex-item-label-${i}`} />
              <Input type="number" placeholder="Annual $" value={it.amount} onChange={e => setItems(items.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} className="h-8 w-36 text-[11px] rounded-sm" data-testid={`opex-item-amount-${i}`} />
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setItems([...items, { label: '', amount: '' }])} className="text-[11px] font-semibold" style={{ color: C.blue }} data-testid="opex-add-item">+ line</button>
            <Input placeholder="Period (e.g. FY2027 budget)" value={period} onChange={e => setPeriod(e.target.value)} className="h-8 w-44 text-[11px] rounded-sm" data-testid="opex-period" />
            <Input placeholder="Where the figure comes from (min 10 chars)" value={note} onChange={e => setNote(e.target.value)} className="h-8 flex-1 min-w-[220px] text-[11px] rounded-sm" data-testid="opex-note" />
            <span className="font-mono text-[11px]" style={{ color: C.navy }} data-testid="opex-form-total">{money(total)}</span>
            <button onClick={save} disabled={busy === 'save' || total <= 0 || note.trim().length < 10} className={btn} style={{ background: C.blue }} data-testid="opex-save">
              {busy === 'save' && <Loader2 className="w-3 h-3 animate-spin" />}{data.recorded ? 'Replace figure' : 'Record figure'}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};
